/* ═══════════════════════════════════════════════════════════════
   DISC READS — the pure rules behind the super-admin "Disc reads" tab
   (design and plan 2026-10-02, Task 2).

   Zed's pricing for analogue discs (2026-10-02) is £1 a disc read, with
   re-reads and failed reads free. He invoices by hand, so the tab shows
   him, per organisation and per UK calendar month, how many discs were
   first read: the number to bill. GET /api/admin/analogue-disc-reads
   counts them; these helpers choose the months and lay the answer out.
   No React, no fetch.

   The months are the UK's (Europe/London), whatever zone the browser is
   in, because the server counts each month between UK midnights. A month
   is found from Intl's UK parts and stepped back with whole numbers,
   never with a Date's own month in the machine's zone: Zed's browser is
   in the UK and the gate's machine is on UTC, and the two disagree on the
   evening of a summer month's last day.
   ═══════════════════════════════════════════════════════════════ */

const UK_MONTH = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: '2-digit',
});

/* The UK year and month an instant falls in, as numbers. */
function ukMonth(now) {
  const parts = UK_MONTH.formatToParts(now);
  const part = (type) => Number(parts.find((p) => p.type === type).value);
  return { year: part('year'), month: part('month') };
}

/* The month `back` months before { year, month }: whole-number arithmetic
   on months counted from year 0, so January steps back to December of the
   year before. */
function monthsBack({ year, month }, back) {
  const index = year * 12 + (month - 1) - back;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/* 'YYYY-MM', the form the endpoint takes. */
function monthValue({ year, month }) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * 'YYYY-MM' of the calendar month before the one `now` falls in, in the UK:
 * the month that is over, and so the one to invoice. The tab opens on it.
 */
export function lastMonth(now = new Date()) {
  return monthValue(monthsBack(ukMonth(now), 1));
}

/* A month's name is read at midnight UTC on its 1st, in UTC. Read in the
   browser's own zone, a zone behind UTC would name the month before. */
const MONTH_WORDS = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' });

/**
 * 'October 2026' for '2026-10'. Anything that is not a 'YYYY-MM' month is
 * left as it came, so a sentence built on it still says something.
 */
export function monthLabel(value) {
  const text = value == null ? '' : String(value);
  const found = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(text);
  if (!found) return text;
  return MONTH_WORDS.format(Date.UTC(Number(found[1]), Number(found[2]) - 1, 1));
}

/**
 * What the tab says about a month: its table's caption, the sentence for a
 * month with no reads, and, for the month still running, the line above
 * its table ('' for a month that is over). A disc bills in the month of its
 * first read, so a month invoiced before it is over would miss the discs
 * read in its last days, and they would never show again: wherever the
 * running month is named, it says it is not over.
 */
export function monthWords(month, running) {
  const label = monthLabel(month);
  if (!running) return { caption: label, empty: `No disc was read in ${label}.`, note: '' };
  return {
    caption: `${label}, so far`,
    empty: `No disc has been read in ${label} so far.`,
    note: `${label} is not over: these are the discs read so far.`,
  };
}

/**
 * The months the picker offers: the UK's current month first, then each
 * month before it, `count` in all, as
 * [{ value: '2026-10', label: 'October 2026 (so far)', current: true },
 *  { value: '2026-09', label: 'September 2026' }, …].
 * The current month is still running, so it is marked `current` and says
 * "so far" (see monthWords). Which month is running is known here, from
 * `now`; monthLabel stays a plain name.
 */
export function monthOptions(now = new Date(), count = 12) {
  const here = ukMonth(now);
  const months = [];
  for (let back = 0; back < count; back += 1) {
    const value = monthValue(monthsBack(here, back));
    months.push(back === 0
      ? { value, label: `${monthLabel(value)} (so far)`, current: true }
      : { value, label: monthLabel(value) });
  }
  return months;
}

/**
 * The price of one disc in words, from the answer's price_pence: '£1' for
 * 100, '£1.50' for 150. The price is the server's, so the screen never
 * hard-codes it; an answer that gives none (or not a whole number of
 * pence) gets '' and the tab says no price, rather than a made-up '£0'.
 */
export function priceWords(pence) {
  if (!Number.isInteger(pence) || pence < 0) return '';
  const pounds = Math.floor(pence / 100);
  const rest = pence % 100;
  return rest === 0 ? `£${pounds}` : `£${pounds}.${String(rest).padStart(2, '0')}`;
}

/* The figures the invoice is made from: billable is the number to bill,
   and filed + waiting + discarded say where those same discs are now, so
   Zed can waive any. Each must be a whole number of discs, 0 or more: one
   read as 0 would take discs off an invoice without a word. */
const BILLED = ['billable', 'filed', 'waiting', 'discarded'];
/* The free reads, shown for context only: one missing reads 0. `trial`
   (design 2026-10-16): discs of a make still on trial, never billed. */
const FREE = ['rereads', 'failed', 'trial'];
/* Every figure, in the table's order. */
const FIGURES = [...BILLED, ...FREE];

const isDiscCount = (value) => Number.isInteger(value) && value >= 0;
const freeCount = (value) => (Number.isFinite(value) ? value : 0);

/**
 * The answer of GET /api/admin/analogue-disc-reads for `month`, laid out
 * for the table: { rows, totals, price }, or null when the answer cannot
 * be read. An answer that cannot be read must never show as a month with
 * no reads, which would be a £0 invoice. It cannot be read when it is not
 * an object, is for another month than `month` (the server echoes the
 * month it counted), has no list of rows, or has a row whose billed
 * figures are not all whole numbers of discs, or whose filed + waiting +
 * discarded is not its billable. Those three are where the billed discs
 * are now, and what Zed waives from, so a row where they do not add up
 * cannot say which discs to waive.
 *
 * The rows keep the server's order (by name), each with a name (its id
 * where it has none) and the free figures 0 where missing. The totals are
 * summed here from those rows, never taken from the answer: the Total row
 * Zed invoices from is the sum of the rows he sees. price is priceWords of
 * price_pence: '' when the answer gave none.
 *
 * Each row carries its own price too (design 2026-10-06): priceWords of the
 * row's price_pence, the price DDIR stored for that organisation, for the
 * Price per disc column; '—' where the row gave none, or none that is a
 * whole number of pence, so the cell is never blank or a made-up '£0'. It
 * bills nothing, so it never makes the answer unreadable: the strict rules
 * above are the billed figures' alone. The answer's own price, for the line
 * under the picker, stays the default.
 */
export function discReadsRows(answer, month) {
  if (!answer || typeof answer !== 'object') return null;
  if (answer.month !== month || !Array.isArray(answer.rows)) return null;
  const rows = [];
  for (const row of answer.rows) {
    if (!row || typeof row !== 'object' || !BILLED.every((key) => isDiscCount(row[key]))) return null;
    if (row.filed + row.waiting + row.discarded !== row.billable) return null;
    const id = row.organisation_id == null ? '' : String(row.organisation_id);
    const name = typeof row.name === 'string' && row.name.trim() ? row.name : id || '—';
    /* The org's trace engine switch (engineFiles): true or false as the
       server said it, null for anything else (it could not read it), which
       the tab draws as no toggle. Never a guess at on. */
    const engineFiles = typeof row.engine_files === 'boolean' ? row.engine_files : null;
    const laid = { organisation_id: id, name, price: priceWords(row.price_pence) || '—', engineFiles };
    for (const key of BILLED) laid[key] = row[key];
    for (const key of FREE) laid[key] = freeCount(row[key]);
    rows.push(laid);
  }
  const totals = {};
  for (const key of FIGURES) totals[key] = rows.reduce((sum, row) => sum + row[key], 0);
  return { rows, totals, price: priceWords(answer.price_pence) };
}

/**
 * What the tab shows for the month on screen and the try on screen (Try
 * again is a new try of the same month), given the last load that landed,
 * { month, attempt, answer } or { month, attempt, error } (the month and
 * the try it was for, and what came back):
 * { show: 'loading' | 'error' | 'empty' | 'table', price, message, rows,
 * totals }.
 *
 * - It is loading until a load for this month AND this try has landed: a
 *   late answer for a month left behind shows the spinner, never its
 *   figures under this month's name, and so does Try again until its
 *   answer lands, never the error it was pressed to clear.
 * - A failure is said in the server's words, and an answer that cannot be
 *   read (discReadsRows) is an error too, never a month with nothing to
 *   invoice.
 * - The price is the last load's, when its answer can be read, whichever
 *   month it was for: the line under the picker keeps it while the next
 *   month loads.
 */
export function discReadsView(month, attempt, landed) {
  const laid = landed && landed.error == null ? discReadsRows(landed.answer, landed.month) : null;
  const price = laid ? laid.price : '';
  if (!landed || landed.month !== month || landed.attempt !== attempt) return { show: 'loading', price };
  const label = monthLabel(month);
  if (landed.error != null) {
    return { show: 'error', price, message: `The disc reads for ${label} could not be loaded: ${landed.error}` };
  }
  if (!laid) {
    return { show: 'error', price, message: `The disc reads for ${label} could not be read from the server's answer.` };
  }
  return { show: laid.rows.length ? 'table' : 'empty', price, rows: laid.rows, totals: laid.totals };
}

/* ── The trace engine's switch per organisation (Zed, 2026-10-15) ──
   Each row's toggle: on, the discs that organisation reads from then on
   are filed from the trace engine's read when it is confident, the AI's
   read the fallback (PUT /api/admin/analogue-engine-files). Off is shadow,
   the AI's read as before, so only turning it ON asks first. */

/** The toggle's words, Zed's. */
export const ENGINE_FILES_LABEL = 'Trace engine files reads';

/**
 * What turning the toggle on asks, for the row's organisation `name`
 * ('this organisation' when it has none).
 */
export function engineFilesConfirm(name) {
  const who = typeof name === 'string' && name.trim() ? name.trim() : 'this organisation';
  return `Discs read from now on for ${who} will be filed from the trace engine's read when it is `
    + "confident; the AI read stays the fallback. Already-filed discs don't change.";
}

/**
 * The last load that landed with organisation `orgId`'s switch set to
 * `enabled`, as the server stored it: a new object, its answer's rows
 * copied, nothing else changed. A load that failed, none at all, or an
 * org not on the answer is returned as it was.
 */
export function withEngineFiles(landed, orgId, enabled) {
  if (!landed || landed.error != null || !landed.answer || !Array.isArray(landed.answer.rows)) return landed;
  const rows = landed.answer.rows.map((row) => (
    row && String(row.organisation_id) === String(orgId) ? { ...row, engine_files: enabled === true } : row));
  return { ...landed, answer: { ...landed.answer, rows } };
}
